// src/components/quickMessage/ContactQmButton.tsx -- the small "Quick msg"
// icon button that appears on a contact row, alongside Pin / Emergency /
// Call. Renders only when the contact has a linked hub_users row (login).
//
// The dashed-blue glow ring is on by design (decision #6 -- always on, no
// "new!" variant). The visual marks the button as the Quick Message entry
// point so it's not lost in the action cluster.

import type { CSSProperties } from "react";
import { QM } from "../../lib/quickMessages";

export interface ContactQmButtonProps {
  onClick: () => void;
  ariaLabel: string;
}

const GLOW_KEYFRAMES = `
@keyframes qm-btn-glow {
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50%      { opacity: 0.85; transform: scale(1.06); }
}
`;

export function ContactQmButton({ onClick, ariaLabel }: ContactQmButtonProps) {
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        aria-label={ariaLabel}
        style={btnStyle}
      >
        <span aria-hidden="true" style={glowStyle} />
        <PlaneIcon />
      </button>
      <style>{GLOW_KEYFRAMES}</style>
    </>
  );
}

function PlaneIcon() {
  return (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke={QM.dark}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path d="M22 2 11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

const btnStyle: CSSProperties = {
  position: "relative",
  width: 38,
  height: 38,
  borderRadius: 11,
  background: QM.bg,
  border: `1.5px solid ${QM.base}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  textDecoration: "none",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.4), 0 2px 6px rgba(61,111,168,0.18)",
  flexShrink: 0,
  padding: 0,
};

const glowStyle: CSSProperties = {
  position: "absolute",
  inset: -5,
  borderRadius: 16,
  border: `2px dashed ${QM.base}`,
  opacity: 0.5,
  pointerEvents: "none",
  animation: "qm-btn-glow 2s ease-in-out infinite",
};
